/**
 * Utility functions for handling dates in Pacific Standard Time (PST)
 */

/**
 * Format a datetime string for activity logs showing date and time in PST
 * @param {string} dateTimeString - ISO datetime string
 * @returns {string} Formatted string like "2/24/2025, 2:30 PM"
 */
export function formatActivityTimePST(dateTimeString) {
  if (!dateTimeString) return '';
  
  try {
    const date = new Date(dateTimeString);
    
    // Format with date and time in PST
    const options = {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting activity time:', error);
    return '';
  }
}

/**
 * Format a date string as M/D/YYYY in PST
 * Handles date-only strings (YYYY-MM-DD) without timezone conversion issues
 * @param {string} dateString - Date string in format YYYY-MM-DD or ISO datetime
 * @returns {string} Formatted date string like "10/15/2025" or empty string
 */
export function formatDatePST(dateString) {
  if (!dateString) return '';
  
  try {
    // If it's a date-only string (YYYY-MM-DD), parse it directly without timezone conversion
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${parseInt(month)}/${parseInt(day)}/${year}`;
    }
    
    // If it's an ISO datetime string, convert to PST
    const date = new Date(dateString);
    
    // Format as PST using toLocaleString with Los Angeles timezone
    const options = {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    };
    
    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format a datetime string with full date and time in PST
 * @param {string} dateTimeString - ISO datetime string
 * @returns {string} Formatted string like "Wednesday, October 15, 2025 at 3:30 PM PST"
 */
export function formatDateTimePST(dateTimeString) {
  if (!dateTimeString) return '';
  
  try {
    const date = new Date(dateTimeString);
    
    // Format with full date and time in PST
    const options = {
      timeZone: 'America/Los_Angeles',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    const formatted = date.toLocaleString('en-US', options);
    return formatted + ' PST';
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '';
  }
}

/**
 * Format a date string as "Month Day, Year" in PST
 * @param {string} dateString - Date string in format YYYY-MM-DD or ISO datetime
 * @returns {string} Formatted string like "October 15, 2025"
 */
export function formatDateLongPST(dateString) {
  if (!dateString) return '';
  
  try {
    // If it's a date-only string (YYYY-MM-DD), parse it directly
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };
      
      return date.toLocaleDateString('en-US', options);
    }
    
    // If it's an ISO datetime, convert to PST
    const date = new Date(dateString);
    
    const options = {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}
